import useGlobalLocation from 'components/globalLocationSelector/hooks/useGlobalLocation';
import { mapYardCheckDetailList } from 'features/yardCheck/mapper';
import { useDataFetcher } from 'hooks/useDataFetcher';
import { useMutation } from 'hooks/useMutation';
import useUserPermission from 'hooks/useUserPermission';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    fetchInventory,
    fetchInventoryColumnsByLocation,
    fetchInventoryFormFieldsByLocation,
    fetchInventoryTypeByLocation,
} from 'services/inventoryService';
import {
    addCheckedItem,
    bulkAddFromInventory,
    editCheckedItem,
    editYardCheck,
    fetchYardCheck,
    fetchYardCheckItemsByYardCheckId,
    removeCheckedItem,
} from 'services/yardCheckService';
import { FEATURE_NAME, TEMP_FIELD_PREFIX, YARD_CHECK_DETAIL_GROUP, YARD_CHECK_ITEM_TYPE, YARD_CHECK_STATUS } from 'utils/constants';
import { groupFieldsByHeader } from 'utils/formFieldHelper';
import { showNotification } from 'utils/notificationsHandler';

const useYardCheckDetail = (yardCheckId) => {
    const { selectedLocationId } = useGlobalLocation();
    const { edit: isYCEditable } = useUserPermission(FEATURE_NAME.YARD_CHECK);
    const [isEditMatchYardCheckItem, setIsEditMatchYardCheckItem] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const inventoryDialogRef = useRef();
    const yardCheckItemRemoveDialogRef = useRef();
    const dataRef = useRef();
    const navigate = useNavigate();

    const { data: yardCheckItem = [], fetchData, isFetching } = useDataFetcher(fetchYardCheckItemsByYardCheckId);
    const { data: inventoryList = [], fetchData: fetchInventoryList } = useDataFetcher(fetchInventory);
    const { data: inventoryFormFields = [], fetchData: fetchInventoryFormFields } = useDataFetcher(fetchInventoryFormFieldsByLocation);
    const { data: inventoryColumns = [], fetchData: fetchInventoryColumns } = useDataFetcher(fetchInventoryColumnsByLocation);
    const { data: yardCheckList = [], fetchData: fetchListYardCheck } = useDataFetcher(fetchYardCheck);
    const { data: inventoryItemType = [], fetchData: fetchInventoryItemType } = useDataFetcher(fetchInventoryTypeByLocation);
    const onAddInventorySuccess = useCallback(() => {
        showNotification({
            title: 'Inventory Check Item(s) added successfully!',
            variant: 'success',
        });
    }, []);

    const onRemoveInventorySuccess = useCallback(() => {
        showNotification({
            title: 'Inventory Check Item(s) removed successfully!',
            variant: 'success',
        });
    }, []);

    const onEditInventorySuccess = useCallback(() => {
        showNotification({
            title: 'Inventory Check Item(s) updated successfully!',
            variant: 'success',
        });
    }, []);

    const { mutate: handleAddYardCheckItem, isLoading: addInventoryLoading } = useMutation({
        mutationFn: addCheckedItem,
        onSuccess: onAddInventorySuccess,
    });
    const { mutate: handleRemoveYardCheckItem, isLoading: removeInventoryLoading } = useMutation({
        mutationFn: removeCheckedItem,
        onSuccess: onRemoveInventorySuccess,
    });
    const { mutate: handleEditYardCheckItem, isLoading: editInventoryLoading } = useMutation({
        mutationFn: editCheckedItem,
        onSuccess: onEditInventorySuccess,
    });
    const mutationLoading = addInventoryLoading || removeInventoryLoading || editInventoryLoading;
    const handleRefresh = useCallback(() => {
        if (selectedLocationId) {
            fetchData([{ locationId: selectedLocationId }], [{ locationId: selectedLocationId }]);
        }
    }, [selectedLocationId, fetchData]);

    const yardCheckInfo = useMemo(() => {
        return yardCheckList?.find((v) => v.id?.toString() === yardCheckId);
    }, [yardCheckId, yardCheckList]);

    const hasAutoStarted = useRef(false);
    useEffect(() => {
        if (isYCEditable && yardCheckInfo?.status === YARD_CHECK_STATUS.OPEN && !hasAutoStarted.current) {
            hasAutoStarted.current = true;
            editYardCheck({ ...yardCheckInfo, status: YARD_CHECK_STATUS.CHECKING }, yardCheckInfo?.id)
                .then(() => {
                    fetchListYardCheck({ locationId: selectedLocationId });
                })
                .catch(() => {
                    hasAutoStarted.current = false;
                    showNotification({
                        title: 'Failed to start inventory check',
                        variant: 'error',
                    });
                });
        }
    }, [isYCEditable, yardCheckInfo, selectedLocationId, fetchListYardCheck]);

    const fetchYardCheckDetailByLocation = useCallback(async () => {
        try {
            setIsDataLoading(true);
            await Promise.all([
                fetchData(yardCheckId),
                fetchInventoryList({ locationId: selectedLocationId }),
                fetchInventoryFormFields({ locationId: selectedLocationId }),
                fetchInventoryColumns({ locationId: selectedLocationId }),
                fetchListYardCheck({ locationId: selectedLocationId }),
                fetchInventoryItemType(selectedLocationId),
            ]);
        } finally {
            setIsDataLoading(false);
        }
    }, [
        fetchData,
        fetchInventoryColumns,
        fetchInventoryFormFields,
        fetchInventoryItemType,
        fetchInventoryList,
        fetchListYardCheck,
        selectedLocationId,
        yardCheckId,
    ]);

    const refetchYardCheckList = useCallback(() => {
        fetchListYardCheck({ locationId: selectedLocationId });
    }, [fetchListYardCheck, selectedLocationId]);

    const yardCheckDetailGroupDataSource = useMemo(() => {
        const checkedInventory = yardCheckItem.map((item) => {
            const existedInventory = inventoryList.find(
                (v) => v.referenceId === item.referenceId && v.inventoryItemType?.id === item.inventoryItemTypeId
            );
            const selectedInventoryItemType = inventoryItemType.find((v) => v.id.toString() === item.inventoryItemTypeId.toString());
            return {
                ...item,
                id: item?.inventorySourceDataId ?? `${TEMP_FIELD_PREFIX}${item?.id}`,
                inventoryItemType: selectedInventoryItemType,
                data: item?.itemData,
                inventoryStatus: item?.inventoryMappingStatus,
                referenceId: item?.referenceId,
                initialInventoryPresence:
                    item?.initialInventoryPresence === YARD_CHECK_ITEM_TYPE.NEW && existedInventory
                        ? YARD_CHECK_ITEM_TYPE.MATCHED
                        : item?.initialInventoryPresence,
                yardCheckItemId: item?.id,
                group: YARD_CHECK_DETAIL_GROUP.CHECKED,
                checkInDate: item?.checkInDate || existedInventory?.checkInDate || yardCheckInfo?.startDate,
            };
        }, []);

        const remainingInventory = inventoryList.reduce((newArr, item) => {
            const isCheckedItem = yardCheckItem?.find(
                (checkedItem) => item.referenceId === checkedItem.referenceId && item.inventoryItemType?.id === checkedItem.inventoryItemTypeId
            );
            if (!isCheckedItem) newArr.push({ ...item, group: YARD_CHECK_DETAIL_GROUP.REMAINING });
            return newArr;
        }, []);

        const checkedInventoryList = checkedInventory?.length
            ? checkedInventory
            : [
                  {
                      id: `${TEMP_FIELD_PREFIX}CHECK`,
                      referenceId: null,
                      group: YARD_CHECK_DETAIL_GROUP.CHECKED,
                  },
              ];

        const remainingInventoryList = remainingInventory?.length
            ? remainingInventory
            : [
                  {
                      id: `${TEMP_FIELD_PREFIX}REMAINING`,
                      referenceId: null,
                      group: YARD_CHECK_DETAIL_GROUP.REMAINING,
                  },
              ];

        return remainingInventoryList.concat(checkedInventoryList);
    }, [inventoryItemType, inventoryList, yardCheckItem, yardCheckInfo]);

    const yardCheckDetailTableData = useMemo(
        () => mapYardCheckDetailList(yardCheckDetailGroupDataSource, inventoryItemType),
        [inventoryItemType, yardCheckDetailGroupDataSource]
    );

    const hasNoLocations = useMemo(() => !selectedLocationId, [selectedLocationId]);
    const handleAddCheckedItemFromRemainingItem = useCallback(
        async (_, item) => {
            if (!item) return;
            const payload = {
                yardCheck: yardCheckInfo,
                inventoryItemTypeId: item.inventoryItemType?.id,
                inventorySourceDataId: item.id || undefined,
                itemData: item?.data,
                inventoryMappingStatus: item.inventoryStatus,
                locationId: selectedLocationId,
                referenceId: item.referenceId || undefined,
                yardCheckId: yardCheckInfo?.id,
            };
            let result = null;
            result = await handleAddYardCheckItem(payload);
            if (result) await fetchYardCheckDetailByLocation();
        },
        [yardCheckInfo, selectedLocationId, handleAddYardCheckItem, fetchYardCheckDetailByLocation]
    );

    const handleAddNewInventory = useCallback(() => {
        setIsEditMatchYardCheckItem(false);
        inventoryDialogRef?.current?.showDialog();
    }, []);

    const handleEditInventory = useCallback((data) => {
        const isHeaderDoubleClick =
            [YARD_CHECK_DETAIL_GROUP.CHECKED, YARD_CHECK_DETAIL_GROUP.REMAINING, 0, 1].includes(data.key) ||
            Array.isArray(data?.items) ||
            Array.isArray(data?.collapsedItems);
        if (isHeaderDoubleClick) return;
        if (data.group === YARD_CHECK_DETAIL_GROUP.REMAINING) return setIsEditMatchYardCheckItem(false);
        setIsEditMatchYardCheckItem([YARD_CHECK_ITEM_TYPE.MATCHED, YARD_CHECK_ITEM_TYPE.UPDATED].includes(data?.initialInventoryPresence));
        inventoryDialogRef?.current?.showDialog(data);
    }, []);

    const handleOnDeleteInventorySuccess = useCallback(() => {
        inventoryDialogRef.current?.hideDialog();
        fetchYardCheckDetailByLocation();
    }, [fetchYardCheckDetailByLocation]);

    const handleRemoveCheckedItem = useCallback((id) => {
        yardCheckItemRemoveDialogRef?.current?.showDialog(id);
    }, []);

    const handleSaveYardCheckItem = useCallback(
        async ({ yardCheckData, referenceId }) => {
            const inventoryValue = yardCheckData;
            const payload = {
                id: inventoryValue?.yardCheckItemId,
                yardCheck: { ...yardCheckInfo, location: { id: yardCheckInfo.locationId } },
                inventoryItemTypeId: inventoryValue?.inventoryItemType?.id,
                inventorySourceDataId: inventoryValue?.id?.toString().includes(TEMP_FIELD_PREFIX) ? undefined : inventoryValue?.id,
                itemData: yardCheckData?.data,
                inventoryMappingStatus: inventoryValue?.inventoryStatus,
                location: { id: selectedLocationId },
                referenceId: referenceId || undefined,
                initialInventoryPresence: inventoryValue?.initialInventoryPresence || undefined,
                action: inventoryValue?.action || undefined,
                yardCheckId: yardCheckInfo.id,
            };
            let result = referenceId
                ? await handleEditYardCheckItem({ yardCheckItemId: inventoryValue?.yardCheckItemId, payload })
                : await handleAddYardCheckItem(payload);

            if (result) {
                inventoryDialogRef?.current?.hideDialog();
                await fetchYardCheckDetailByLocation();
            }
        },
        [fetchYardCheckDetailByLocation, handleAddYardCheckItem, handleEditYardCheckItem, selectedLocationId, yardCheckInfo]
    );

    const remainingOptions = useMemo(() => {
        return yardCheckDetailTableData?.reduce((arr, item) => {
            if (item.id?.toString()?.includes(TEMP_FIELD_PREFIX)) return arr;
            if (item.group === YARD_CHECK_DETAIL_GROUP.REMAINING) arr.push({ ...item, value: item.id, label: item.referenceId });
            return arr;
        }, []);
    }, [yardCheckDetailTableData]);

    const handleNavigateToYardCheckListPage = useCallback(() => {
        navigate(`/yard-check-list`);
    }, [navigate]);

    const handleNavigateToSummaryPage = useCallback(() => {
        navigate(`/yard-check-summary/${yardCheckId}`);
    }, [navigate, yardCheckId]);

    const updateYardCheckDetail = useCallback(async () => {
        if (yardCheckInfo?.status === YARD_CHECK_STATUS.OPEN) {
            await editYardCheck({ ...yardCheckInfo, status: YARD_CHECK_STATUS.CHECKING }, yardCheckInfo?.id);
            refetchYardCheckList();
        } else {
            handleNavigateToSummaryPage();
        }
    }, [handleNavigateToSummaryPage, refetchYardCheckList, yardCheckInfo]);

    const handleMarkInventoryItem = useCallback(
        async (itemIdOrIds, status) => {
            try {
                const inventorySourceDataIds = Array.isArray(itemIdOrIds) ? itemIdOrIds : [itemIdOrIds];
                const payload = {
                    yardCheckId,
                    inventorySourceDataIds,
                    initialStatus: status,
                };

                await bulkAddFromInventory(payload);
                await fetchYardCheckDetailByLocation();
                onAddInventorySuccess();
                return true;
            } catch (error) {
                showNotification({
                    title: `Failed to mark as ${status}`,
                    variant: 'error',
                });
                return false;
            }
        },
        [yardCheckId, fetchYardCheckDetailByLocation, onAddInventorySuccess]
    );

    return {
        tableData: yardCheckDetailTableData,
        isLoading: isDataLoading,
        isFetching,
        hasNoLocations,
        fetchYardCheckDetailByLocation,
        handleRefresh,
        fetchInventoryList,
        inventoryList,
        inventoryFormFields,
        tableColumns: groupFieldsByHeader(inventoryColumns),
        fetchInventoryFormFields,
        selectedLocationId,
        yardCheckInfo,
        inventorySourceData: inventoryList,
        handleAddYardCheckItem,
        handleRemoveYardCheckItem,
        handleEditYardCheckItem,
        yardCheckDetailGroupDataSource,
        refetchYardCheckList,
        mutationLoading,
        remainingOptions,
        handleAddCheckedItemFromRemainingItem,
        handleAddNewInventory,
        handleEditInventory,
        handleRemoveCheckedItem,
        handleSaveYardCheckItem,
        isEditMatchYardCheckItem,
        handleOnDeleteInventorySuccess,
        handleNavigateToYardCheckListPage,
        handleNavigateToSummaryPage,
        updateYardCheckDetail,
        inventoryDialogRef,
        yardCheckItemRemoveDialogRef,
        dataRef,
        onAddInventorySuccess,
        yardCheckItem,
        handleMarkInventoryItem,
    };
};

export default useYardCheckDetail;
